import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Input from '../../components/input/Input'
import { Controller, useForm } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks'
import { setUser } from '../../redux/slices/UserSlice/UserSlice'
import { useNavigate } from 'react-router'
import { apiGetServerUser } from '../../api/services/getserveruser/getserveruser.service'
import { ApiGetServerUserResponse, ErrorList, ServerList } from 'src/api'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography
} from '@mui/material'
import { selectUser } from '../../redux/slices/UserSlice/UserSlice.selectors'
import { useCookies } from 'react-cookie'
import { LoadingButton } from '@mui/lab'
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded'
import useSentry from 'src/hooks/useSentry'
import PersonManagementImg from 'src/assets/images/person_management.svg'
import AvailroomWhiteLogoImg from 'src/assets/images/logo_availroom_blanco2020.svg'
import axios from 'axios'
import { toast } from 'react-toastify'

interface formValues {
  userName: string
  password: string
  rememberMe: boolean
}

const Auth = () => {
  const { t } = useTranslation()
  const { handleSubmit, control, setError } = useForm<formValues>()

  // redux hooks
  const dispatch = useAppDispatch()
  const { token: userToken } = useAppSelector(selectUser)

  const { captureError } = useSentry()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [openSelectServerModal, setOpenSelectServerModal] = useState(false)
  const [selectedEnvironment, setSelectedEnvironment] = useState('')
  const [environmentList, setEnvironmentList] = useState<ServerList[]>([])
  const [_cookie, setCookie] = useCookies(['languagecookie'])
  const [loginData, setLoginData] = useState<{
    serverUserData: ApiGetServerUserResponse
    userData: formValues
  }>()

  const makeLoginRequest = (serverUrl: string, data: formValues) => {
    return axios.put<{
      language: string
    }>(
      `https://${serverUrl}/sialdPMS/web/resourcescrossorigin/webcrossorigin/login`,
      data,
      {
        withCredentials: true
      }
    )
  }

  const loginToServer = async (
    resData: ApiGetServerUserResponse,
    data: formValues
  ) => {
    await makeLoginRequest(resData.serverUrl, data)
    const loginRes = await makeLoginRequest(resData.serverUrl, data)
    const loginResData = loginRes.data
    setCookie('languagecookie', loginResData.language, {
      sameSite: 'none',
      secure: true,
      domain: '.availroom.com',
      path: '/'
    })
    dispatch(
      setUser({
        rememberMe: data.rememberMe,
        name: resData.name,
        serverList: resData.serverList,
        serverUrl: resData.serverUrl,
        surname: resData.surname,
        token: resData.token,
        userName: data.userName
      })
    )
    navigate('/')
  }

  const onSubmit = handleSubmit(async (data) => {
    setLoading(true)
    try {
      const response = await apiGetServerUser(data)
      const resData = response
      if (!resData.ok) {
        setLoading(false)
        throw response
      }
      const isRootUser = !resData.serverUrl
      if (isRootUser) {
        setEnvironmentList(resData.serverList)
        setLoginData({
          serverUserData: resData,
          userData: data
        })
        setLoading(false)
        return setOpenSelectServerModal(true)
      }
      await loginToServer(resData, data)
    } catch (error: any) {
      console.log(error)
      setLoading(false)
      if ('errorList' in error) {
        const errorList = error.errorList as ErrorList[]
        errorList.forEach(({ field, error }) => {
          if (field === 'userName') {
            return setError('userName', {
              message: error,
              type: 'validate'
            })
          }
          if (field === 'password') {
            console.log('here')
            return setError('password', {
              message: error,
              type: 'validate'
            })
          }
        })
        return
      }
      toast.error(t('alerts.An_error_has_ocurred_try_again'))
      captureError(error, {
        description: 'error from handleOnSubmit Auth.tsx'
      })
    }
  })

  const handleSubmitEnvironment = async () => {
    try {
      setLoading(true)
      const response = await apiGetServerUser({
        ...loginData!.userData,
        serverPath: selectedEnvironment
      })
      await loginToServer(response, loginData!.userData)
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (userToken) {
      navigate('/')
    }
  }, [])

  return (
    <Box>
      <Box
        sx={{
          display: 'flex'
        }}
      >
        <Box
          sx={{
            height: '100vh',
            width: '50%',
            backgroundColor: '#2C2CE7',
            display: 'grid',
            placeItems: 'center',
            position: 'relative'
          }}
        >
          <Box
            component={'img'}
            src={AvailroomWhiteLogoImg}
            sx={{
              position: 'absolute',
              left: '2rem',
              top: '2rem',
              width: '30%',
              maxWidth: '250px'
            }}
          />
          <Box
            component={'img'}
            src={PersonManagementImg}
            sx={{
              width: '70%',
              maxWidth: 700
            }}
          />
        </Box>

        <Box
          sx={{
            minHeight: '100vh',
            width: '50%',
            boxSizing: 'border-box',
            paddingY: '3rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2rem',
            position: 'relative'
          }}
        >
          <Box
            component='aside'
            aria-label='Webinar de presentación de Availroom'
            sx={{
              width: 'calc(100% - 3rem)',
              maxWidth: 680,
              boxSizing: 'border-box',
              padding: { xs: '1.5rem', md: '2rem' },
              borderRadius: '1.25rem',
              backgroundColor: '#F1F2FF',
              border: '1px solid #E1E3F4',
              color: '#202036',
              display: 'flex',
              alignItems: 'flex-start',
              gap: { xs: '1rem', md: '2rem' },
              textAlign: 'left'
            }}
          >
            <Box
              component='time'
              dateTime='2026-10-29'
              aria-label='29 de octubre de 2026'
              sx={{
                width: { xs: 64, md: 88 },
                paddingY: '1rem',
                flexShrink: 0,
                borderRadius: '0.875rem',
                backgroundColor: '#E5E7FF',
                color: '#4338CA',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Typography
                component='span'
                aria-hidden='true'
                sx={{ fontSize: '0.875rem', fontWeight: 700, letterSpacing: '0.15em' }}
              >
                OCT
              </Typography>
              <Typography
                component='span'
                aria-hidden='true'
                sx={{
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  lineHeight: 1,
                  fontWeight: 700,
                  letterSpacing: '-0.04em'
                }}
              >
                29
              </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#4338CA',
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  letterSpacing: '0.14em'
                }}
              >
                WEBINAR · AVAILROOM
              </Typography>
              <Typography
                component='h2'
                sx={{
                  marginTop: '1rem',
                  fontSize: { xs: '1.5rem', md: '1.75rem' },
                  lineHeight: 1.3,
                  fontWeight: 700,
                  letterSpacing: '-0.035em'
                }}
              >
                Availroom da el salto.
              </Typography>
              <Typography
                sx={{
                  marginTop: '0.5rem',
                  fontSize: { xs: '1.125rem', md: '1.375rem' },
                  lineHeight: 1.4
                }}
              >
                Tu PMS entra en una nueva fase.
              </Typography>
              <Typography
                sx={{
                  marginTop: '1.25rem',
                  fontSize: '1.125rem',
                  color: '#656575'
                }}
              >
                Descubre la nueva interfaz el 29 de octubre.
              </Typography>
              <Typography
                component='a'
                href='/lanzamiento/index.html'
                target='_blank'
                rel='noopener noreferrer'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.625rem',
                  marginTop: '2rem',
                  color: '#4338CA',
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  textDecoration: 'none',
                  textUnderlineOffset: '0.2em',
                  overflowWrap: 'anywhere',
                  '&:hover': { textDecoration: 'underline' },
                  '&:focus-visible': {
                    outline: '3px solid #4338CA',
                    outlineOffset: '4px'
                  }
                }}
              >
                Ver presentación y detalles
                <ArrowOutwardRoundedIcon sx={{ fontSize: '1.25rem', flexShrink: 0 }} />
              </Typography>
              <Typography
                component='a'
                href='https://availroom.com/lanzamiento?utm_source=zoho&utm_medium=email&utm_campaign=lanzamiento-oct-2026'
                target='_blank'
                rel='noopener noreferrer'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: 'fit-content',
                  maxWidth: '100%',
                  gap: '0.625rem',
                  marginTop: '0.5rem',
                  color: '#4338CA',
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  textDecoration: 'none',
                  textUnderlineOffset: '0.2em',
                  overflowWrap: 'anywhere',
                  '&:hover': { textDecoration: 'underline' },
                  '&:focus-visible': {
                    outline: '3px solid #4338CA',
                    outlineOffset: '4px'
                  }
                }}
              >
                Inscríbete al webinar en directo
                <ArrowOutwardRoundedIcon sx={{ fontSize: '1.25rem', flexShrink: 0 }} />
              </Typography>
            </Box>
          </Box>
          <Box
            component='form'
            onSubmit={onSubmit}
            sx={{
              width: '70%',
              maxWidth: 500
            }}
          >
            <Typography
              variant='h6'
              sx={{
                color: ({ palette }) => palette.text.primaryLight
              }}
            >
              {t('texts.Welcome_to_AvailRoom')}
            </Typography>
            <Box
              sx={{
                marginTop: '3rem'
              }}
            >
              <Controller
                control={control}
                name='userName'
                rules={{
                  required: {
                    message: t('validations.required'),
                    value: true
                  }
                }}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label={t('form.labels.User(email)')}
                    error={!!error}
                    helperText={error?.message}
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name='password'
                rules={{
                  required: {
                    message: t('validations.required'),
                    value: true
                  }
                }}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    sx={{ marginTop: '1rem' }}
                    type='password'
                    label={t('form.labels.Password')}
                    error={!!error}
                    helperText={error?.message}
                    {...field}
                  />
                )}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                marginTop: '1rem'
              }}
            >
              <Controller
                control={control}
                name='rememberMe'
                render={({ field: { value, onChange, ...field } }) => (
                  <FormControlLabel
                    checked={value}
                    {...field}
                    control={
                      <Checkbox
                        onChange={(e) => {
                          onChange(e.target.checked)
                        }}
                      />
                    }
                    label={t('labels.Remember_my_data')}
                  />
                )}
              />
            </Box>
            <LoadingButton
              loading={loading}
              fullWidth
              variant='contained'
              size='large'
              type='submit'
              sx={{
                marginTop: '1rem'
              }}
            >
              {t('buttons.Access')}
            </LoadingButton>
          </Box>
        </Box>
      </Box>
      {/* EXPECTING TO BE CHANGED */}
      <Modal open={openSelectServerModal}>
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            backgroundColor: 'white',
            boxShadow: 3,
            transform: 'translate(-50%, -50%)',
            padding: '2rem',
            width: '400px',
            height: 300
          }}
        >
          <Typography>{t('labels.Select_Environment')}</Typography>
          <FormControl
            fullWidth
            sx={{
              marginTop: '1rem'
            }}
          >
            <InputLabel id='demo-simple-select-label'>
              {t('labels.Environment')}
            </InputLabel>
            <Select
              labelId='demo-simple-select-label'
              id='demo-simple-select'
              value={selectedEnvironment}
              label={t('labels.Environment')}
              onChange={(e) => {
                setSelectedEnvironment(e.target.value!)
              }}
            >
              {environmentList.map((environment) => (
                <MenuItem
                  value={environment.serverPath}
                  key={environment.serverPath}
                >
                  {environment.serverPath}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box
            sx={{
              marginTop: '1rem'
            }}
          >
            <LoadingButton
              loading={loading}
              variant='contained'
              onClick={handleSubmitEnvironment}
            >
              {t('buttons.Accept')}
            </LoadingButton>
          </Box>
        </Box>
      </Modal>
    </Box>
  )
}

export default Auth
